// Directions and drive times: the Drive screen and the Directions button on
// every place. Google draws the map through its keyless embed (the iframe that
// Google Maps' own Share → Embed a map produces; the official Maps Embed API
// needs a key on a billed project, which Kinmantle never uses). Drive times and
// the chain laws and closures on the way come from CDOT COtrip (Colorado only),
// and street addresses from calendar events are placed with the U.S. Census
// geocoder (free, no key).

import type { RoadEvent } from "./mountains";

export interface Point {
  lat: number;
  lon: number;
}

/** Somewhere to drive to: a point, or an address as written (calendar events). */
export type DriveTarget = { name: string } & ({ lat: number; lon: number; address?: undefined } | { address: string; lat?: undefined; lon?: undefined });

/** What the Worker knows about the drive to one destination. */
export interface DriveInfo {
  /** CDOT's routing estimate, minutes (Colorado only; no live traffic). */
  minutes: number | null;
  km: number | null;
  /** Chain/traction laws and closures within a few km of the route. */
  roads: RoadEvent[];
  /** Where an address was placed, when it could be. */
  at: Point | null;
}

export interface DriveRequest {
  to: ({ id: string } & ({ lat: number; lon: number } | { address: string }))[];
}

export interface DrivePayload {
  routes: Record<string, DriveInfo>;
  fetchedAt: string;
}

export const MAX_DRIVE_TARGETS = 30;

/** A drive request from the browser, checked; throws with a plain message when it isn't one. */
export function parseDriveRequest(raw: unknown): DriveRequest {
  const to = (raw as { to?: unknown } | null)?.to;
  if (!Array.isArray(to) || to.length === 0 || to.length > MAX_DRIVE_TARGETS) throw new Error(`Ask for 1 to ${MAX_DRIVE_TARGETS} destinations`);
  return {
    to: to.map((t: Record<string, unknown>) => {
      const id = typeof t?.id === "string" && t.id.length <= 200 ? t.id : null;
      if (!id) throw new Error("Each destination needs an id");
      if (typeof t.address === "string" && t.address.trim() && t.address.length <= 300) return { id, address: t.address.trim() };
      const lat = Number(t.lat);
      const lon = Number(t.lon);
      if (typeof t.lat !== "number" || typeof t.lon !== "number" || Math.abs(lat) > 90 || Math.abs(lon) > 180) throw new Error("Each destination needs a place or an address");
      return { id, lat, lon };
    }),
  };
}

// Google's embed takes its route in "pb" form: !-separated fields, with ! and * escaped.
const pbText = (s: string) => encodeURIComponent(s.trim()).replace(/\*/g, "*2A").replace(/!/g, "*21").replace(/%20/g, "+");
const pbPoint = (p: Point) => `!3m2!1d${p.lat}!2d${p.lon}`;

/** The keyless Google Maps directions embed, from `from` to the target, for an iframe. */
export function directionsEmbedUrl(from: Point, to: DriveTarget): string {
  const origin = `!4m3${pbPoint(from)}`;
  const dest = to.address !== undefined ? `!4m1!2s${pbText(to.address)}` : `!4m3${pbPoint(to)}`;
  // Field 4m holds origin and destination; its count is the number of tokens inside.
  const inner = `${origin}${dest}`;
  const count = inner.split("!").length - 1;
  return `https://www.google.com/maps/embed?origin=mfe&pb=!1m${count + 1}!4m${count}${inner}`;
}

/** The full Google Maps directions link, for a phone (shown as a QR code on the wall). */
export function directionsLink(from: Point | null, to: DriveTarget): string {
  const dest = to.address !== undefined ? to.address : `${to.lat},${to.lon}`;
  const q = new URLSearchParams({ api: "1", ...(from ? { origin: `${from.lat},${from.lon}` } : {}), destination: dest, travelmode: "driving" });
  return `https://www.google.com/maps/dir/?${q}`;
}

/** Calendar locations that aren't somewhere to drive: meeting links, "Zoom", phone calls. */
export function isDrivable(location: string | null | undefined): location is string {
  if (!location) return false;
  const t = location.trim();
  if (t.length < 3) return false;
  if (/https?:\/\/|www\.|\.(com|org|net|us|io)\b/i.test(t)) return false;
  return !/^(zoom|google meet|meet|teams|microsoft teams|webex|facetime|online|virtual|remote|phone|call|tbd|tba|home)\b/i.test(t);
}

/**
 * Street-address forms of a calendar location for the Census geocoder, which
 * wants "number street, city, state": Google writes "Place, 123 Main St,
 * Town, CO 80000, USA", so the place name and country are dropped.
 */
export function addressCandidates(location: string): string[] {
  const parts = location
    .replace(/\s+/g, " ")
    .split(",")
    .map((p) => p.trim())
    .filter(Boolean);
  while (parts.length && /^(usa|us|united states( of america)?)$/i.test(parts[parts.length - 1]!)) parts.pop();
  const start = parts.findIndex((p) => /^\d+[a-z]?\s+\S/i.test(p));
  if (start < 0 || parts.length - start < 2) return [];
  return [parts.slice(start).join(", ")];
}

/** Every route point at least `km` from the last one kept (and the end), so matching road events stays cheap. */
export function thinPath(path: [number, number][], km: number): [number, number][] {
  if (path.length <= 2) return path;
  const out: [number, number][] = [path[0]!];
  for (const p of path.slice(1, -1)) {
    const last = out[out.length - 1]!;
    const dx = (p[1] - last[1]) * 111.32 * Math.cos((p[0] * Math.PI) / 180);
    const dy = (p[0] - last[0]) * 110.57;
    if (Math.hypot(dx, dy) >= km) out.push(p);
  }
  out.push(path[path.length - 1]!);
  return out;
}

export function driveText(minutes: number | null): string | null {
  if (minutes === null) return null;
  return minutes < 60 ? `${minutes} min` : `${Math.floor(minutes / 60)} h ${String(minutes % 60).padStart(2, "0")} min`;
}

export const ROAD_LABEL: Record<RoadEvent["kind"], string> = { closure: "Closed", chain: "Chain law", traction: "Traction law" };

/** Trucks-only rules don't change a family's drive. */
export const affectsCars = (e: RoadEvent) => !/commercial/i.test(e.title);
